import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H1, H3, Text } from '@amzn/stencil-react-components/text';
import { Link } from '@amzn/stencil-react-components/link';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { IconInformationFillSmall, IconProductAzaLarge } from '@amzn/stencil-react-icons';
import { AIIconWrapper } from '@amzn/stencil-react-components/ai';

import { useMessages } from '../../shared/i18n/useMessages';
import {
  PromptContextSection,
  PROMPT_MIN_LENGTH,
} from '../../features/audit-whiz/components/PromptContextSection';
import { SopReferenceSection } from '../../features/audit-whiz/components/SopReferenceSection';

const PAGE_BUNDLE = 'features.audit-whiz.translations.generateQuestions';

type PageKey =
  | 'breadcrumbCurrent'
  | 'betaBadge'
  | 'promptSectionTitle'
  | 'promptSectionDescription'
  | 'sopSourceBadge'
  | 'sopSourceBadgeLink'
  | 'switchToManualPrompt'
  | 'switchToManualLink'
  | 'cancelButton'
  | 'generateButton'
  | 'generateDisabledHint';

/** Questions list route — Cancel and the breadcrumb return here. */
const QUESTIONS_LIST_ROUTE = '/question';
/** Manual question-creation flow — the "switch to manually" link target. */
const MANUAL_CREATE_ROUTE = '/question/question-edit';
/** Review & Publish screen — where the generate action lands (Phase 1 shell). */
const REVIEW_ROUTE = '/question/generate/review';

/**
 * "Create Questions with Astra" generation screen (AuditWhiz WHIZ-001).
 *
 * Phase 1 UI shell: renders the prompt-and-context card and the SOP
 * reference card with page-level actions. No backend calls or agent
 * integration — "Generate questions" is a no-op placeholder until Phase 3.
 *
 * Rendered on desktop through `V2Adapter` (see AppRouter.tsx), which
 * supplies the Stencil, i18n, and auth providers this page needs.
 */
export function GenerateQuestionsPage(): JSX.Element {
  const navigate = useNavigate();
  const { t, f } = useMessages<PageKey>(PAGE_BUNDLE);

  const [prompt, setPrompt] = useState('');
  const [sopLink, setSopLink] = useState<string | null>(null);

  const canGenerate = prompt.trim().length >= PROMPT_MIN_LENGTH;

  const handleAddSopLink = useCallback((url: string) => setSopLink(url), []);
  const handleRemoveSopLink = useCallback(() => setSopLink(null), []);

  const handleCancel = useCallback(() => {
    void navigate(QUESTIONS_LIST_ROUTE);
  }, [navigate]);

  const handleSwitchToManual = useCallback(() => {
    void navigate(MANUAL_CREATE_ROUTE);
  }, [navigate]);

  const handleGenerate = useCallback(() => {
    // Phase 1 shell — no backend call yet. The real flow will fire the
    // agent request here and navigate on response (Phase 2/3). For now we
    // navigate straight to the Review & Publish screen, which renders
    // hardcoded mock questions.
    void navigate(REVIEW_ROUTE);
  }, [navigate]);

  return (
    <Col
      gridGap="dimensions.spacing.300"
      width="100%"
      padding={{
        top: 'dimensions.spacing.300',
        bottom: 'dimensions.spacing.600',
      }}
      data-testid="generate-questions-page"
    >
      {/* Breadcrumb is provided by the V1 desktop shell (AppBreadcrumbs),
            driven by the route path — no in-page breadcrumb here. */}

      {/* Title */}
      <Row gridGap="dimensions.spacing.200" alignItems="center">
        <span data-testid="generate-questions-astra-icon">
          <AIIconWrapper>
            <IconProductAzaLarge aria-hidden="true" />
          </AIIconWrapper>
        </span>
        <H1>{t('breadcrumbCurrent')}</H1>
        <Badge type={BadgeType.Primary} value={t('betaBadge')} />
      </Row>

      {/* One merged card: the prompt and the SOP reference live together under
          a single "Define your audit questions" header, with the Source badge
          in the card's top-right (driven by whether an SOP link is set). */}
      <Card padding="dimensions.spacing.300">
        <Col gridGap="dimensions.spacing.300" width="100%">
          <Row justifyContent="space-between" alignItems="flex-start">
            <Col gridGap="dimensions.spacing.100" flex={1} minWidth={0}>
              <H3>{t('promptSectionTitle')}</H3>
              <Text color="color.text.secondary">{t('promptSectionDescription')}</Text>
            </Col>
            <Badge
              type={sopLink !== null ? BadgeType.Positive : BadgeType.Neutral}
              value={
                sopLink !== null
                  ? f('sopSourceBadgeLink', { url: sopLink })
                  : t('sopSourceBadge')
              }
              data-testid="generate-questions-source-badge"
            />
          </Row>

          <PromptContextSection
            value={prompt}
            onChange={setPrompt}
            dataTestId="generate-questions-prompt-input"
          />

          <SopReferenceSection
            link={sopLink}
            onAddLink={handleAddSopLink}
            onRemoveLink={handleRemoveSopLink}
            dataTestId="generate-questions-sop"
          />
        </Col>
      </Card>

      {/* Footer actions */}
      <Row justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Row gridGap="dimensions.spacing.100" alignItems="center">
          <Text color="color.text.secondary">{t('switchToManualPrompt')}</Text>
          <Link
            onClick={handleSwitchToManual}
            data-testid="generate-questions-switch-manual"
          >
            {t('switchToManualLink')}
          </Link>
        </Row>

        <Col gridGap="dimensions.spacing.50" alignItems="flex-end">
          <Row gridGap="dimensions.spacing.200" alignItems="center">
            <Button
              variant={ButtonVariant.Tertiary}
              onClick={handleCancel}
              data-testid="generate-questions-cancel"
            >
              {t('cancelButton')}
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              disabled={!canGenerate}
              onClick={handleGenerate}
              data-testid="generate-questions-generate"
            >
              {t('generateButton')}
            </Button>
          </Row>
          {!canGenerate && (
            <Row
              gridGap="dimensions.spacing.50"
              alignItems="center"
              data-testid="generate-questions-disabled-hint"
            >
              <IconInformationFillSmall aria-hidden="true" />
              <Text fontSize="T100" color="color.text.secondary">
                {t('generateDisabledHint')}
              </Text>
            </Row>
          )}
        </Col>
      </Row>
    </Col>
  );
}
